import dataProvider, { fetchAllData, sleep } from "../../dataProvider";
import { ccaDropOffLocationType, IncludeParentOptionEnum, ScheduleTypeEnum } from "../students/const";
import { IncludeStudentOptionEnum } from "./conts";
import { uniq } from "lodash";
import moment from "moment/moment";
import {
  ccaScheduleDropOffTypeFormatter,
  getDaysOfWeek,
  getScheduleTypeLabel
} from "../students/tools";

export const exportCommercialDocumentValidation = (values: formExportCommercialDocumentValues) => {
  if (!values.commercialDocumentType) {
    return true;
  }

  if (!values.startDate || !values.endDate) {
    return true;
  }

  if (
    values.isFilterByStatus &&
    (!values.status || values.status.length === 0)
  ) {
    return true;
  }

  if (
    values.isFilterBySchool &&
    !values.schoolId
  ) {
    return true;
  }

  if (
    values.isFilterByDescription &&
    !values.description
  ) {
    return true;
  }

  if (
    values.isIncludeParent &&
    !values.includeParentOption
  ) {
    return true;
  }

  if (
    values.isIncludeStudent &&
    !values.includeStudentOption
  ) {
    return true;
  }

  if (
    values.isIncludeStudent &&
    values.includeStudentOption === IncludeStudentOptionEnum.StudentWithSchedulePeriod &&
    !values.scheduleType
  ) {
    return true;
  }

  if (
    values.isIncludeStudent &&
    values.includeStudentOption === IncludeStudentOptionEnum.StudentWithSchedulePeriod &&
    values.scheduleType === ScheduleTypeEnum.Both &&
    (!values.academicYearId || !values.ccaAcademicYearId)
  ) {
    return true;
  }

  if (
    values.isIncludeStudent &&
    values.includeStudentOption === IncludeStudentOptionEnum.StudentWithSchedulePeriod &&
    values.scheduleType === ScheduleTypeEnum.Regular &&
    !values.academicYearId
  ) {
    return true;
  }

  if (
    values.isIncludeStudent &&
    values.includeStudentOption === IncludeStudentOptionEnum.StudentWithSchedulePeriod &&
    values.scheduleType === ScheduleTypeEnum.Cca &&
    !values.ccaAcademicYearId
  ) {
    return true;
  }

  return false;
}

export const generateExportCommercialDocumentFilename = (values: formExportCommercialDocumentValues) => {
  // Initialize base prefix for the file name.
  let fileNamePrefix = `${values.commercialDocumentTypeLabel} Records Issued from ${values.startDate} to ${values.endDate}`;

  // Initialize an array to collect all parts to include in the file name based on toggle selections
  const suffixParts: string[] = [];

  // Add "Student Schedule" if the toggle is enabled
  if (values.isIncludeStudent) {
    if (values.includeStudentOption === IncludeStudentOptionEnum.StudentOnly) {
      suffixParts.push('Student');
    } else if (values.includeStudentOption === IncludeStudentOptionEnum.StudentWithSchedulePeriod) {
      suffixParts.push('Student');
      suffixParts.push('Schedule Period');
    }
  }

  // Add Parent info if the toggle is enabled
  // and based on parent option selection
  if (values.isIncludeParent) {
    if (values.includeParentOption === IncludeParentOptionEnum.ParentOnly) {
      suffixParts.push('Parent');
    } else if (values.includeParentOption === IncludeParentOptionEnum.ParentWithGuardian) {
      suffixParts.push('Parent');
      suffixParts.push('Guardian');
    }
  }

  // Construct the suffix string if any toggle was enabled
  const suffix = suffixParts.length > 0 ? ` with ${suffixParts.join(' and ')}` : '';

  // Final filename composed of prefix, suffix, and the school name
  return `${fileNamePrefix}${suffix}.csv`;
}

const formatCentsToDollar = (priceInCents: number): string =>
  `$${(priceInCents / 100).toFixed(2)}`;

// Helper function to process ALL schedules for a student
// and return them in a row format for display/export.
const attachStudentScheduleToRow = (
  baseStudent: any,
  studentData: any,
  academicYear: any,
  academicTerm: any,
  ccaAcademicYear: any,
  ccaAcademicTerm: any,
  ccaFixedLocationDropOffs: any,
  ccaWaves: any
) => {
  const scheduleRows: any[] = [];

  // Extract schedules safely, defaulting to empty arrays if missing
  const regularSchedules = studentData?.regularStudentSchedules ?? [];
  const ccaSchedules = studentData?.ccaStudentSchedules ?? [];

  // Process each regular schedule
  regularSchedules.forEach((regularSchedule: any) => {
    const regularScheduleRow = createScheduleRow(
      baseStudent,
      regularSchedule,
      academicYear,
      academicTerm,
      ccaAcademicYear,
      ccaAcademicTerm,
      ccaFixedLocationDropOffs,
      ccaWaves,
      ScheduleTypeEnum.Regular
    );

    scheduleRows.push(regularScheduleRow);
  });

  // Process each CCA schedule
  ccaSchedules.forEach((ccaSchedule: any) => {
    const ccaScheduleRow = createScheduleRow(
      baseStudent,
      ccaSchedule,
      academicYear,
      academicTerm,
      ccaAcademicYear,
      ccaAcademicTerm,
      ccaFixedLocationDropOffs,
      ccaWaves,
      ScheduleTypeEnum.Cca
    );

    scheduleRows.push(ccaScheduleRow);
  });

  // If no schedules exist, add a empty row
  if (
    studentData.regularStudentSchedules.length === 0 &&
    studentData.ccaStudentSchedules.length === 0
  ) {
    const emptyScheduleRow: any = {
      ...baseStudent,
      academicYearId: "",
      academicYearName: "",
      ccaAcademicYearId: "",
      ccaAcademicYearName: "",
      academicTermId: "",
      academicTermName: "",
      ccaAcademicTermId: "",
      ccaAcademicTermName: "",
      isTaking: "No",
      scheduleType: "None",
      schedule: "None",
      datePeriod: "",
      pickupAddress: "",
      dropOffAddress: "",
      ccaDoorToDoor: "",
      ccaFixedLocationMonday: "",
      waveMonday: "",
      ccaFixedLocationTuesday: "",
      waveTuesday: "",
      ccaFixedLocationWednesday: "",
      waveWednesday: "",
      ccaFixedLocationThursday: "",
      waveThursday: "",
      ccaFixedLocationFriday: "",
      waveFriday: "",
      daysOfWeek: "",
    };

    scheduleRows.push(emptyScheduleRow);
  }

  return scheduleRows;
}

// Helper to transforms a single student schedule into rows
const createScheduleRow = (
  baseStudent: any,
  schedule: any,
  academicYear: any,
  academicTerm: any,
  ccaAcademicYear: any,
  ccaAcademicTerm: any,
  ccaFixedLocationDropOffs: any,
  ccaWaves: any,
  type: ScheduleTypeEnum,
) => {
  const isCca = type === ScheduleTypeEnum.Cca;
  const isRegular = type === ScheduleTypeEnum.Regular;

  // Determine which address columns to show (AM: pickUpAddress; PM: dropOffAddress)
  const showPickupAddress = isRegular && schedule.isTaking && schedule?.detail?.isToSchool;
  const showDropOffAddress = isRegular && schedule.isTaking && schedule?.detail?.isFromSchool;

  // Determine which CCA fixed location is selected as taking
  const isFixedLocationMonday = isCca && schedule.isTaking && schedule.dropOffType === "fixed_location" && schedule.detail.isMonday;
  const isFixedLocationTuesday = isCca && schedule.isTaking && schedule.dropOffType === "fixed_location" && schedule.detail.isTuesday;
  const isFixedLocationWednesday = isCca && schedule.isTaking && schedule.dropOffType === "fixed_location" && schedule.detail.isWednesday;
  const isFixedLocationThursday = isCca && schedule.isTaking && schedule.dropOffType === "fixed_location" && schedule.detail.isThursday;
  const isFixedLocationFriday = isCca && schedule.isTaking && schedule.dropOffType === "fixed_location" && schedule.detail.isFriday;

  // Build academic year and term fields
  const academicFields = {
    academicYearId: isRegular ? academicYear.id : "",
    academicYearName: isRegular ? academicYear.name : "",
    academicTermId: isRegular ? schedule.termId : "",
    academicTermName: isRegular ? academicTerm[schedule.termId] : "",
    ccaAcademicYearId: isCca ? ccaAcademicYear.id : "",
    ccaAcademicYearName: isCca ? ccaAcademicYear.name : "",
    ccaAcademicTermId: isCca ? schedule.termId : "",
    ccaAcademicTermName: isCca ? ccaAcademicTerm[schedule.termId] : "",
  };

  // Generate schedule label
  let scheduleLabel = "None";
  if (isRegular) {
    scheduleLabel = getScheduleTypeLabel(
      schedule?.detail?.isFromSchool,
      schedule?.detail?.isToSchool
    );
  } else if (isCca && schedule.isTaking) {
    scheduleLabel = ccaScheduleDropOffTypeFormatter(schedule.dropOffType, ccaDropOffLocationType);
  }

  // return row for the schedule
  return {
    ...baseStudent,
    ...academicFields,
    isTaking: schedule.isTaking ? "Yes" : "No",
    scheduleType: isCca ? "CCA" : "Regular",
    schedule: scheduleLabel,
    datePeriod: schedule.isTaking
      ? `${moment(schedule.startDate).format("DD/MM/YYYY")} - ${moment(schedule.endDate).format("DD/MM/YYYY")}`
      : "",
    pickupAddress: showPickupAddress
      ? `${schedule.detail.blockNumber} ${schedule.detail.street} ${schedule.detail.unitNumber ?? ""} ${schedule.detail.building ?? ""} ${schedule.detail.postalCode}`
      : "",
    dropOffAddress: showDropOffAddress
      ? `${schedule.detail.blockNumber} ${schedule.detail.street} ${schedule.detail.unitNumber ?? ""} ${schedule.detail.building ?? ""} ${schedule.detail.postalCode}`
      : "",
    ccaDoorToDoor: isCca && schedule.isTaking && schedule.dropOffType === "door_to_door"
      ? `${schedule.detail.doorToDoorAddress.blockNumber} ${
        schedule.detail.doorToDoorAddress.street
      } ${schedule.detail.doorToDoorAddress.unitNumber ?? ""} ${
        schedule.detail.doorToDoorAddress.building ?? ""
      } ${schedule.detail.doorToDoorAddress.postalCode}`
      : "",
    ccaFixedLocationMonday: isFixedLocationMonday
      ? ccaFixedLocationDropOffs[schedule.detail.fixedLocationMondayId]
      : "",
    waveMonday: isFixedLocationMonday ? ccaWaves[schedule.detail.waveMondayId] : "",
    ccaFixedLocationTuesday: isFixedLocationTuesday
      ? ccaFixedLocationDropOffs[schedule.detail.fixedLocationTuesdayId]
      : "",
    waveTuesday: isFixedLocationTuesday ? ccaWaves[schedule.detail.waveTuesdayId] : "",
    ccaFixedLocationWednesday: isFixedLocationWednesday
      ? ccaFixedLocationDropOffs[schedule.detail.fixedLocationWednesdayId]
      : "",
    waveWednesday: isFixedLocationWednesday ? ccaWaves[schedule.detail.waveWednesdayId] : "",
    ccaFixedLocationThursday: isFixedLocationThursday
      ? ccaFixedLocationDropOffs[schedule.detail.fixedLocationThursdayId]
      : "",
    waveThursday: isFixedLocationThursday ? ccaWaves[schedule.detail.waveThursdayId] : "",
    ccaFixedLocationFriday: isFixedLocationFriday
      ? ccaFixedLocationDropOffs[schedule.detail.fixedLocationFridayId]
      : "",
    waveFriday: isFixedLocationFriday ? ccaWaves[schedule.detail.waveFridayId] : "",
    daysOfWeek: schedule.isTaking ? getDaysOfWeek(schedule.detail) : "",
  };
}

/**
 * Transforms a commercial document record into one or more rows
 * for CSV output, based on the toggled options (e.g., parent info and student schedules).
 *
 * Depending on the provided flags, this function:
 * - Includes parent and guardian data if requested
 * - Expands each student schedule into one or more rows
 * - A base row if no schedules exist
 *
 * @param record - The commercial document record to be transformed
 * @param scheduleType - Selected schedule type to export (Regular, CCA, or Both)
 * @param parentOption - Enum option to include parent only or parent with guardian
 * @param studentOption - Enum option to include student only or student with schedule
 * @param academicYear - Selected academic year for regular schedules
 * @param academicTerm - Lookup map of termId -> term name (for regular)
 * @param ccaAcademicYear - Selected academic year for CCA schedules
 * @param ccaAcademicTerm - Lookup map of ccaTermId -> term name (for CCA)
 * @param ccaFixedLocationDropOffs - Lookup map of fixedLocationId -> fixed location name
 * @param ccaWaves - Lookup map of waveId -> wave name
 * @param isIncludeParent - Flag to include parent and/or guardian data
 * @param isIncludeStudent - Flag to include student and/or schedule data
 * @param documentType - Exported document type
 * @param signal - AbortSignal to support cancellation during async processing
 * @returns An array of exportable rows (CSV-ready)
 */
const commercialDocumentReportTransform = async (
  record: ICommercialDocument,
  parentOption: IncludeParentOptionEnum,
  studentOption: IncludeStudentOptionEnum,
  scheduleType: ScheduleTypeEnum,
  academicYear: any,
  academicTerm: any,
  ccaAcademicYear: any,
  ccaAcademicTerm: any,
  ccaFixedLocationDropOffs: any,
  ccaWaves: any,
  isIncludeParent: boolean,
  isIncludeStudent: boolean,
  documentType: string,
  signal: AbortSignal
) => {
  // throw an error if the operation is canceled
  if (signal.aborted) {
    throw new Error("Operation aborted");
  }

  try {
    const studentIds: number[] = [];
    const isIncludeStudentSchedulePeriod = isIncludeStudent && studentOption === IncludeStudentOptionEnum.StudentWithSchedulePeriod;
    let parentFields = {};
    let studentMap: Record<string, any> = {}; // key: commercialDocumentItemId, value: student
    let studentSchedulesMap: Record<number, any[]> = {}; // key: studentId, value: studentData
    let taggedToFields = {};

    if (documentType === "CreditNote") {
      taggedToFields = {
        taggedTo: record.invoiceTaggedMbcRef ?? "",
      }
    }

    if (isIncludeParent || isIncludeStudent) {
      // Get additional parent and student data
      const { data: additionalData } = await dataProvider.customGetList("commercialDocumentWithParentAndStudent", {
        filter: {
          CommercialDocumentId: record.id,
          IsIncludeParent: isIncludeParent,
          IsIncludeStudent: isIncludeStudent,
        }
      });

      // Handle parent data
      if (isIncludeParent) {
        const parentData = additionalData?.parent;
        const guardianData = parentData?.alternateContacts?.[0] ?? null;
        const parentRow = {
          parentId: parentData?.id ?? "",
          mbcCustomerId: parentData?.mbcCustomerId ?? "",
          parentName: parentData ? `${parentData.firstName} ${parentData.lastName}` : "",
          parentContact: parentData ? `${parentData.firstName} ${parentData.lastName}` : "",
          parentEmail: parentData?.email ?? "",
        }

        switch (parentOption) {
          case IncludeParentOptionEnum.ParentOnly:
            parentFields = {
              ...parentRow
            };
            break;
          case IncludeParentOptionEnum.ParentWithGuardian:
            parentFields = {
              ...parentRow,
              guardianName: guardianData?.name ?? "",
              guardianRelationship: guardianData?.relationship ?? "",
              guardianContact: guardianData?.contactNumber ?? "",
              guardianEmail: guardianData?.email ?? "",
            };
            break;
        }
      }

      // Handle student data
      if (isIncludeStudent && Array.isArray(additionalData?.students)) {
        // create mapping from commercialDocumentItemIds -> student object
        // Each student can be linked to multiple item IDs
        for (const student of additionalData.students) {
          for (const itemId of student.commercialDocumentItemIds) {
            studentMap[itemId] = student;
          }

          // Create list of student ids
          studentIds.push(student.id);
        }

        // get student schedule for each unique student id (if needed)
        if (isIncludeStudentSchedulePeriod) {
          // create unique student id
          // then loop over each unique student id and
          // get their schedule data
          const uniqueStudentIds = uniq(studentIds);
          for (const studentId of uniqueStudentIds) {
            const { data } = await dataProvider.customGetList("studentMasterListData", {
              filter: {
                StudentId: studentId,
                IsIncludeStudentSchedule: true,
                AcademicYearId: academicYear.id,
                CcaAcademicYearId: ccaAcademicYear.id,
                ScheduleType: scheduleType,
              },
            });

            // create mapping from studentIds -> student schedule data
            studentSchedulesMap[studentId] = data;

            // Delay next request
            await sleep(200);
          }
        }
      }
    }

    return record.items.flatMap((item: ICommercialDocumentItem) => {
      // Used to store rows for this item
      // (one per student/schedule, or just a commercial document with parent row
      // if no student is included)
      let dataRows: any[] = [];

      const baseDocumentRow = {
        id: record.id,
        mbcRef: record.mbcRef,
        ...taggedToFields,
        issuedAt: record.issuedAt,
        status: record.status,
        description: item.description,
        amount: formatCentsToDollar(record.subTotal),
        amountIncludeVat: formatCentsToDollar(record.totalDue),
        userId: record.userId,
        ...parentFields,
        schoolCode: record.schoolCode,
      }

      // Handle student data if needed
      if (isIncludeStudent) {
        // find which student is linked to this commercial document item
        const matchedStudent = studentMap[item.id];

        // If there are matching student, include the student data to the row
        if (matchedStudent) {
          // Base student data that's always included if student data is requested
          const baseStudent = {
            studentId: matchedStudent.id ?? "",
            externalId: matchedStudent.externalId ?? "",
            studentName: matchedStudent
              ? `${matchedStudent.firstName} ${matchedStudent.lastName}`
              : "",
          };

          // Add row(s) depending on the student option
          switch (studentOption) {
            case IncludeStudentOptionEnum.StudentOnly:
              // only need basic student info (no schedule details)
              // Push a single row combining:
              //  - common commercial document fields
              //  - optional parent fields
              //  - base student info
              dataRows.push({
                ...baseDocumentRow,
                ...baseStudent,
              });
              break;

            case IncludeStudentOptionEnum.StudentWithSchedulePeriod:
              // need student info + one row for each schedule period
              // create rows that have schedule details for this student
              const studentSchedules = attachStudentScheduleToRow(
                baseStudent, // base student data
                studentSchedulesMap[matchedStudent.id], // student's schedules
                academicYear,
                academicTerm,
                ccaAcademicYear,
                ccaAcademicTerm,
                ccaFixedLocationDropOffs,
                ccaWaves
              );

              // Add one row per schedule (each gets the same commercial document data)
              studentSchedules.forEach((scheduleRow) => {
                dataRows.push({
                  ...baseDocumentRow,
                  ...scheduleRow,
                });
              });
              break;
          }
        }
      }

      // Make sure still output at least ONE row for the item
      // even if student data isn't included OR no matching student was found.
      if (dataRows.length === 0) {
        dataRows.push({
          ...baseDocumentRow,
        });
      }

      // return merged rows into the final array
      return dataRows;
    });
  } catch (error) {
    throw new Error(
      `Failed to fetch commercial document with related data. ${error.message}. Please try again in a few moments`
    );
  }
};

/**
 * Processes records for CSV export, chunking data and fetching additional information as needed
 * based on the specified resource type. A callback function is called to track loading progress.
 *
 * @param body - A form data object.
 * @param records - An array of records to process.
 * @param callback - A function to handle the loading state and progress updates.
 * @returns An array containing processed data ready for CSV export.
 */
export const processCommercialDocumentRecordsForCsv = async (
  body: any,
  records: any[],
  callback: (progressUpdate: {
    isLoading?: boolean;
    loadedRecordCount: number;
    totalRecords: number;
  }) => void,
) => {
  // Split the records array into chunks of the specified size
  const chunkSize = 100;
  // if isIncludeParent or isIncludeStudent is true, we need to chunk the request to get additional data
  // otherwise, process everything at once
  const enableChunking = body.isIncludeParent || body.isIncludeStudent;
  const chunks = enableChunking
    ? Array.from(
      { length: Math.ceil(records.length / chunkSize) },
      (_, i) => records.slice(i * chunkSize, i * chunkSize + chunkSize)
    )
    : [records];

  // const chunks = [records];

  let processedData: any[] = [];

  // Create an abort controller to handle cancellation of tasks
  const abortController = new AbortController();
  const signal = abortController.signal;

  let academicTerm = {};
  let academicYear = {};
  let ccaAcademicYear = {};
  let ccaAcademicTerm = {};
  let ccaFixedLocationDropOffs = {};
  let ccaWaves = {};
  if (body.isIncludeStudent && body.includeStudentOption === IncludeStudentOptionEnum.StudentWithSchedulePeriod) {
    if (
      body.scheduleType === ScheduleTypeEnum.Regular ||
      body.scheduleType === ScheduleTypeEnum.Both
    ) {
      // Get academic year detail and assign academicYearLabel
      const academicYearResponse = await dataProvider.getOne(
        "academicYears",
        {
          id: body.academicYearId,
        }
      );
      academicYear = academicYearResponse.data;

      const academicTermResponse = await fetchAllData(
        "academicTerms",
        {
          AcademicYearId: body.academicYearId
        }
      );
      academicTerm = Object.fromEntries(
        academicTermResponse.map((academicTerm: IAcademicTerm) => [
          academicTerm.id,
          academicTerm.name,
        ])
      );
    }

    if (
      body.scheduleType === ScheduleTypeEnum.Cca ||
      body.scheduleType === ScheduleTypeEnum.Both
    ) {
      // Get academic year detail

      const ccaAcademicYearResponse = await dataProvider.getOne(
        "ccaAcademicYears",
        {
          id: body.ccaAcademicYearId,
        }
      );
      ccaAcademicYear = ccaAcademicYearResponse.data;

      const ccaFixedLocationResponse = await fetchAllData(
        "ccaFixedLocationDropOffs",
        {
          SchoolId: body.schoolId
        }
      );
      ccaFixedLocationDropOffs = Object.fromEntries(
        ccaFixedLocationResponse.map((fixedLocation: ICcaFixedLocationDropOff) => [
          fixedLocation.id,
          fixedLocation.name,
        ]),
      );

      const ccaAcademicTermResponse = await fetchAllData(
        "ccaAcademicTerms",
        {
          CcaAcademicYearId: body.ccaAcademicYearId
        }
      );
      ccaAcademicTerm = Object.fromEntries(
        ccaAcademicTermResponse.map(
          (ccaAcademicTerm: ICcaAcademicTerm) => [
            ccaAcademicTerm.id,
            ccaAcademicTerm.name,
          ]
        )
      );

      const ccaWaveResponse = await fetchAllData("busSchedules", {
        SchoolId: body.schoolId,
      });
      ccaWaves = Object.fromEntries(
        ccaWaveResponse.map((ccaWave: IBusSchedule) => [
          ccaWave.id,
          ccaWave.name,
        ]),
      );
    }
  }

  // Loop over each chunk to process records in manageable batches
  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    const chunk = chunks[chunkIndex];

    // Process all records within the chunk concurrently
    const chunkResults = await Promise.all(
      chunk.map(async (record, recordIndex) => {
        // Exit if the operation is canceled
        if (signal.aborted) return;

        // Optional delay each request by 100ms * recordIndex within the chunk,
        // Only if chunking
        if (enableChunking) {
          await sleep(recordIndex * 100);
        }

        // Calculate the current record index for progress tracking and
        // update the progress using the callback
        const currentIndex = chunkIndex * chunkSize + recordIndex + 1;
        callback({
          loadedRecordCount: currentIndex,
          totalRecords: records.length,
        });

        // Process records based on the export type
        const parentDataOption = body.isIncludeParent ? body.includeParentOption : null;
        const studentDataOption = body.isIncludeStudent ? body.includeStudentOption : null;
        return commercialDocumentReportTransform(
          record,
          parentDataOption,
          studentDataOption,
          body.scheduleType,
          academicYear,
          academicTerm,
          ccaAcademicYear,
          ccaAcademicTerm,
          ccaFixedLocationDropOffs,
          ccaWaves,
          body.isIncludeParent,
          body.isIncludeStudent,
          body.commercialDocumentType,
          signal
        );
      }),
    );

    // Append processed chunk results to the main processed data array
    processedData = [...processedData, ...chunkResults.flat()];
  }

  // Return the fully processed data for CSV export
  // And add sequential "no" column in the first column
  return processedData.map((record, i) => {
    return {
      no: i + 1,
      ...record,
    }
  });
};